// MasterComponent.tsx — master page, plug in new masters via MASTER_CONFIGS only
import "rsuite/DatePicker/styles/index.css";
import { useEffect, useState } from "react";
import { getUserDetails, getReasonCodeDetails, getMasterDetails, editMasterDetails, deleteMasterDetails, addMasterDetails } from "~/services/supervisor.services";
import { mastersRoute } from "~/pages/all-routes";
import { toast } from "react-toastify";
import MastersTable from "./masterTable";
import { MASTER_CONFIGS } from "./masterConfigs";

const API_MAP: Record<string, () => Promise<{ data?: any; status: boolean; error?: any }>> = {
  users: () => getMasterDetails("users"),
  group: () => getMasterDetails("group"),
  reasoncodes: () => getMasterDetails("reasoncodes"),
};

const SAVE_MAP: Record<string, (row: any, isNew?: boolean) => Promise<any>> = {
  users: async (row, isNew) => { console.log("save user", row, isNew); return true; },
  group: async (row) => { const res = await editMasterDetails(row, row?.id, "group"); return res.data; },
  reasoncodes: async (row, isNew) => { console.log("save reason", row, isNew); return true; },
};

const CREATE_MAP: Record<string, (row: any, isNew?: boolean) => Promise<any>> = {
  users: async (row, isNew) => { console.log("save user", row, isNew); return true; },
  group: async (row) => { const res = await addMasterDetails(row, "group"); return res.data; },
  reasoncodes: async (row, isNew) => { console.log("save reason", row, isNew); return true; },
};

const DELETE_MAP: Record<string, (id: string) => Promise<any>> = {
  users: async (id) => { console.log("delete user", id); return true; },
  group: async (id) => { const res = await deleteMasterDetails(id, "group"); return res.data; },
  reasoncodes: async (id) => { console.log("delete reason", id); return true; },
};

const MasterComponent = () => {
  const { master_name } = mastersRoute.useSearch();
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const config = MASTER_CONFIGS[master_name];

  const fetchData = async () => {
    if (!config) return;
    setIsLoading(true);
    try {
      const fetchFn = API_MAP[config.apiKey];
      console.log(fetchFn, "fetchFn")
      if (!fetchFn) return toast.error("No API configured for this Master", { theme: "colored" });
      const { data: res, status } = await fetchFn();
      if (status && res) setData((res as any).data ?? res);
    } catch {
      toast.error("Failed to load data", { theme: "colored" });
    } finally {
      setIsLoading(false);
    }
  };


  // Reset + refetch on master switch
  useEffect(() => {
    setData([]);
    void fetchData();
  }, [master_name]);

  if (!config) {
    return <div className="p-4 text-red-500">Unknown master: "{master_name}"</div>;
  }

  const withRefresh = async (apiCall: () => Promise<any>): Promise<any> => {
    const res = await apiCall();
    await new Promise((r) => setTimeout(r, 200));
    await fetchData();
    return res;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h4 className="text-2xl font-semibold text-orange-500">{config.title}</h4>
      </div>

      <div className="bg-neutral-100 rounded-lg p-3">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <div className="animate-spin border-4 border-orange-400 border-t-transparent w-12 h-12 rounded-full" />
            <p className="text-gray-500 text-sm">Loading {config.title}...</p>
          </div>
        ) : (
          <MastersTable
            config={config}
            data={data}
            onDataUpdate={setData}

            onSave={(row, isNew) =>
              withRefresh(() =>
                isNew
                  ? CREATE_MAP[config.apiKey](row)
                  : SAVE_MAP[config.apiKey](row)
              )
            }

            onDelete={async(id:string) =>
              withRefresh(() =>
                DELETE_MAP[config.apiKey](id)
              )
            }

            onBulkDelete={(ids: (string)[]) =>
              withRefresh(() =>
                Promise.all(
                  ids.map(id => DELETE_MAP[config.apiKey](id))
                )
              )
            }
          />
        )}
      </div>
    </div>
  );
};

export default MasterComponent;
